import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useSWR from 'swr';
import { Layout, LoadingSpinner, ErrorMessage } from '@/shared/components';
import { useFormSubmission } from '@/shared/hooks/useFormSubmission';
import { pricesApi } from '@/shared/api';
import {
  priceCategorySchema,
  type PriceCategoryFormData
} from '@/shared/schemas';

/**
 * Страница редактирования категории цен
 * Форма для изменения существующей категории с валидацией
 */
export const PriceCategoryEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const categoryId = id ? parseInt(id, 10) : 0;

  // Загрузка данных категории
  const {
    data: categoryResponse,
    error: loadError,
    isLoading
  } = useSWR(
    categoryId ? ['price-category', categoryId] : null,
    () => pricesApi.getCategoryById(categoryId),
    { revalidateOnFocus: false }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<PriceCategoryFormData>({
    resolver: zodResolver(priceCategorySchema),
    defaultValues: {
      name: '',
      sortOrder: 0,
      isActive: true
    }
  });

  /**
   * Заполнение формы данными категории при загрузке
   */
  useEffect(() => {
    if (categoryResponse?.success && categoryResponse.category) {
      const category = categoryResponse.category;
      reset({
        name: category.name,
        sortOrder: category.sortOrder,
        isActive: category.isActive
      });
    }
  }, [categoryResponse, reset]);

  /**
   * Хук для управления отправкой формы с уведомлениями
   */
  const { isSubmitting, handleSubmit: handleFormSubmit } =
    useFormSubmission<PriceCategoryFormData>({
      onSubmit: async (data: PriceCategoryFormData) => {
        const response = await pricesApi.updateCategory(categoryId, data);

        if (response.success) {
          // Перенаправляем на страницу цен через 2 секунды
          setTimeout(() => {
            navigate('/prices');
          }, 2000);
        }
      },
      successMessage: 'Категория успешно обновлена',
      errorMessage: 'Ошибка при обновлении категории'
    });

  /**
   * Обработчик отмены
   */
  const handleCancel = (): void => {
    navigate('/prices');
  };

  // Проверка валидности ID
  if (!categoryId || isNaN(categoryId)) {
    return (
      <Layout title="Ошибка">
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <ErrorMessage message="Неверный ID категории" />
          </div>
        </div>
      </Layout>
    );
  }

  // Состояние загрузки
  if (isLoading) {
    return (
      <Layout title="Загрузка...">
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" text="Загрузка данных категории..." />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Ошибка загрузки
  if (loadError || !categoryResponse?.success) {
    return (
      <Layout title="Ошибка">
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <ErrorMessage message="Ошибка загрузки данных категории. Попробуйте обновить страницу." />
          </div>
        </div>
      </Layout>
    );
  }

  const category = categoryResponse.category;

  return (
    <Layout title={`Редактировать: ${category.name}`}>
      <div className="space-y-6">
        {/* Заголовок */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-base-content">
              Редактировать категорию цен
            </h2>
            <p className="text-base-content/60 mt-1">
              Изменение категории "{category.name}"
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            <i className="fas fa-arrow-left"></i>
            Назад к списку
          </button>
        </div>

        {/* Форма */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="space-y-6"
            >
              {/* Название */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Название категории <span className="text-error">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className={`input input-bordered w-full ${errors.name ? 'input-error' : ''}`}
                  placeholder="Например: Консультации педиатра"
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.name.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Порядок сортировки */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Порядок сортировки
                  </span>
                </label>
                <input
                  type="number"
                  {...register('sortOrder', { valueAsNumber: true })}
                  className={`input input-bordered w-full ${errors.sortOrder ? 'input-error' : ''}`}
                  placeholder="0"
                  min="0"
                  disabled={isSubmitting}
                />
                {errors.sortOrder && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.sortOrder.message}
                    </span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Чем меньше число, тем выше в списке будет отображаться
                    категория
                  </span>
                </label>
              </div>

              {/* Статус активности */}
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="checkbox checkbox-primary"
                    disabled={isSubmitting}
                  />
                  <span className="label-text font-medium">
                    Активная категория
                  </span>
                </label>
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Неактивные категории не отображаются на сайте
                  </span>
                </label>
              </div>

              {/* Информация о категории */}
              <div className="bg-base-200 p-4 rounded-lg">
                <h4 className="font-medium text-base-content mb-2">
                  Информация о категории
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-base-content/60">
                      Услуг в категории:
                    </span>
                    <span className="ml-2 font-medium">
                      {category.priceItems?.length || 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-base-content/60">Создана:</span>
                    <span className="ml-2 font-medium">
                      {new Date(category.createdAt).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Кнопки действий */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  type="submit"
                  className="btn btn-primary flex-1 sm:flex-none"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Сохранение...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Сохранить изменения
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-secondary flex-1 sm:flex-none"
                  disabled={isSubmitting}
                >
                  <i className="fas fa-times"></i>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};
